import { StaffVideosCard } from "../staff-videos";
import { journeyModeOf } from "../../shared/dto";
import { translateCount } from "../i18n";
import { formatDate } from "./core";
import type { StaffPaneProps } from "./console-types";
import type { ClientSectionKey, StaffRole } from "./model";
import { AuditPane, NotificationsPane } from "./ConsolePanels";
import { DfyBuildCard } from "./DfyBuild";
import { PromptRebuildCard } from "./PromptRebuildCard";
import { PaymentsSetupFlagCard } from "./PaymentsSetupCard";
import {
  ClientEditor,
  MilestoneEditor,
  RegressionEditor,
  StableCallLinksCard
} from "./Editors";
import { OwnerEmailCard } from "./OwnerEmailCard";
import { ClientTestClassification } from "./ClientTestClassification";
import { PartnerBriefPanel } from "./PartnerBriefPanel";
import { PartnerAssign } from "./PartnerAssign";
import { BackfillCard, CrmCard, MilestoneList, NotifyClientCard } from "./Operations";
import { PurchaseLicenseCard } from "./PurchaseLicenses";
import { ResourcesPane } from "./Resources";
import "./client-tools.css";

/**
 * F138a (Pavel 2026-08-11): "Celý tady ty nástroje klienta vlastně jsou
 * strašně nepochopitelný. Mám pocit, že jsou to starý nástroje, na který jsi
 * naplácal ty nové... starý rozhraní jsi nechal pod tím novým rozhraním."
 *
 * He was describing a measured fact. The detail carried a drawer ("Nástroje
 * klienta"), nine tabs inside it, and a SECOND drawer ("Technické údaje
 * klienta") inside the first tab: four levels deep, 6 430 px to reach the
 * client's own e-mail address, which the sidebar prints for free. Seven of the
 * nine tabs showed panels the step workspace above them already draws,
 * unfiltered - the old console kept alive by a handful of things that lived
 * nowhere else.
 *
 * So the detail is ONE surface with THREE FLAT sections below the work:
 *
 *   Přehled klienta · Aktivita · Nastavení
 *
 * No tabs, no drawers, no nesting - the client-side law (F138 direction,
 * principle 2: depth never exceeds plocha -> sekce). Each section head carries
 * the client's own page anatomy: eyebrow, ONE h2, lead.
 *
 * Every card that left named where it went (audit sekce 7, rows 1-28); nothing
 * here is new. What died: the six tabs the step workspace already draws, the
 * "Cesta klienta" list two hundred pixels from the rail that draws it better,
 * the BuddyPro instance and Materiály za klienta cards (their own steps own
 * them), the third copy of the confirmation table (F104 gave the control to
 * each step and the full list to Předání), the unexplained "Posun stavu"
 * (a second, blind interface over the endpoint "Zpětné doplnění" already
 * drives), and the second CRM card.
 */
export const CLIENT_SECTION_KEYS: readonly ClientSectionKey[] = ["overview", "activity", "settings"];

export function clientSectionElementId(section: ClientSectionKey): string {
  return `client-section-${section}`;
}

export type ClientSectionsProps = Omit<StaffPaneProps, "onOpenSection">;

export function ClientSections(props: ClientSectionsProps) {
  return (
    <div className="client-sections" data-client-sections="true">
      <ClientOverviewSection {...props} />
      <ClientActivitySection {...props} />
      <ClientSettingsSection {...props} />
    </div>
  );
}

function ClientSection(props: {
  section: ClientSectionKey;
  t: (key: string) => string;
  /** A quiet link belonging to the SECTION, never a card of its own (F138 k3). */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const titleId = `${clientSectionElementId(props.section)}-title`;
  return (
    <section
      className="client-section"
      data-client-section={props.section}
      id={clientSectionElementId(props.section)}
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <header className="client-section-head">
        <p className="panel-label">{props.t(`staffConsole.clientSection.${props.section}.eyebrow`)}</p>
        <h2 id={titleId}>{props.t(`staffConsole.clientSection.${props.section}.title`)}</h2>
        <p className="client-section-lead">{props.t(`staffConsole.clientSection.${props.section}.lead`)}</p>
        {props.action}
      </header>
      <div className="detail-grid">{props.children}</div>
    </section>
  );
}

/**
 * Six cards where fifteen used to be (audit sekce 1). Order is the partner's
 * own reading order: where the client stands, what the build was told, what
 * has been produced, and only then the two records (milestones, CRM).
 */
export function ClientOverviewSection(props: ClientSectionsProps) {
  return (
    <ClientSection section="overview" t={props.t}>
      {/* 🔴 F138 kolečko 3: "Kde to je" is DELETED, and its vocabulary with it.
          Three of its four lines were printed elsewhere on the same screen -
          "Krok 15 z 17 · Dotazník k appce" in the eyebrow, in the rail AND in
          the card, "Další hovor: Zatím nic" in the card and in the right
          column - so a partner read the same sentence three times and could
          not tell which one was the fact. The one thing it owned, the count of
          open notes, moved to the head of Aktivita, which is where those notes
          are (baseline #2, verdict fault 3). */}
      {/* The videos card renders ONLY when this client has a video: an empty
          dashed box on a premium position is capacity spent on nothing, and
          configuring videos lives in Servis, so nothing is lost by silence
          (verdict fault 4). The step workspace keeps the honest empty state -
          there the video IS the step. */}
      <StaffVideosCard clientId={props.detail.client.id} locale={props.locale} t={props.t} hideWhenEmpty />
      {/* Five fields wide: the briefing takes the whole row (client-tools.css)
          rather than standing as a 1 200 px column beside two three-line
          cards. F106 asks for it on a VISIBLE place before anything is
          generated, and full width is the most visible place there is. */}
      <PartnerBriefPanel
        detail={props.detail}
        locale={props.locale}
        t={props.t}
        run={props.run}
      />
      {/* 🔴 One column, not a grid. Every surviving block here is a full-width
          stack, so a two-track grid could only ever pair a tall one with a
          short one: the DFY list stood eleven hundred pixels tall beside a
          three-line "Milníky" and the pair read as a card that failed to load
          (verdict faults 6 and 7). The rule is in client-tools.css, scoped to
          this section. */}
      <DfyBuildCard {...paneProps(props)} />
      <PromptRebuildCard {...paneProps(props)} />
      <article className="staff-card" data-overview-milestones="true">
        <h3>{props.t("staffConsole.milestones")}</h3>
        <MilestoneList items={props.detail.milestones} t={props.t} locale={props.locale} />
      </article>
      {/* F155: what the client BOUGHT, read live from the platform. It sits
          beside the CRM record because both answer questions about the purchase
          that our own database cannot, and both degrade to one quiet line when
          the integration behind them is off or silent. NOT the licence key in
          Nastavení: that one is what the Instance Creator built and pushed back
          (F105), and the two cards name themselves apart. */}
      <PurchaseLicenseCard clientId={props.detail.client.id} t={props.t} locale={props.locale} />
      {/* Row 24 of the parity table: the CRM record was drawn TWICE on one
          page (sidebar + the nested drawer). It belongs to the client's
          overview, once - and when it cannot load it says so in one quiet
          line instead of a full card (verdict fault 8). */}
      <CrmCard clientId={props.detail.client.id} t={props.t} locale={props.locale} />
    </ClientSection>
  );
}

/** Rows 9 and 10: the audit trail and, for an admin, the notification queue -
 * the only two panes of the old drawer that no step owns. */
export function ClientActivitySection(props: ClientSectionsProps) {
  const openNotes = props.detail.feedback.filter((row) => String(row.status) === "new").length;
  return (
    <ClientSection
      section="activity"
      t={props.t}
      action={openNotes > 0 && props.onOpenStep ? (
        // The one fact "Kde to je" owned, in the section it is about, as a
        // quiet link rather than a card: the notes are the client's own voice
        // and the partner's next move, so the head both counts them and goes
        // there (verdict fault 3).
        <button
          className="quiet-link client-section-action"
          data-open-notes={openNotes}
          type="button"
          onClick={() => props.onOpenStep?.("feedback")}
        >
          {translateCount(props.locale, "staffConsole.clientSection.activity.openNotes", openNotes)}
        </button>
      ) : undefined}
    >
      <article className="staff-card client-activity-card">
        <h3>{props.t("staffConsole.clientSection.activity.auditTitle")}</h3>
        <AuditPane {...paneProps(props)} />
      </article>
      {props.role === "admin" && (
        <article className="staff-card client-activity-card">
          <h3>{props.t("staffConsole.clientSection.activity.notificationsTitle")}</h3>
          <NotificationsPane {...paneProps(props)} />
        </article>
      )}
    </ClientSection>
  );
}

/** Rows 6, 19, 20, 22, 23, 25-28: everything that CHANGES this client rather
 * than reporting on them. The admin editors keep their admin-only gate. */
export function ClientSettingsSection(props: ClientSectionsProps) {
  const pane = paneProps(props);
  return (
    <ClientSection section="settings" t={props.t}>
      <ClientAssignmentCard {...props} />
      <article className="staff-card client-resources-card">
        <h3>{props.t("staffConsole.clientSection.settings.resourcesTitle")}</h3>
        <ResourcesPane {...pane} />
      </article>
      <StableCallLinksCard callLinks={props.detail.callLinks} t={props.t} />
      {/* Row 21: "Posun stavu" is dead. It drove the SAME endpoint as this
          card, with no explanation and no preview - two interfaces over one
          transition. The one that says what it does survives. */}
      <BackfillCard {...pane} />
      <NotifyClientCard {...pane} />
      {props.role === "admin" && <ClientEditor key={props.detail.client.id} {...pane} />}
      {props.role === "admin" && <ClientTestClassification key={`test-classification-${props.detail.client.id}`} clientId={props.detail.client.id} {...pane} />}
      {/* F171: give a client the "Napojení plateb" step (manual imports). */}
      {props.role === "admin" && <PaymentsSetupFlagCard key={`payments-flag-${props.detail.client.id}`} {...pane} />}
      {props.role === "admin" && (
        <OwnerEmailCard key={`owner-email-${props.detail.client.id}`} {...pane} />
      )}
      {props.role === "admin" && <RegressionEditor {...pane} />}
      {props.role === "admin" && <MilestoneEditor {...pane} />}
    </ClientSection>
  );
}

/**
 * 🔴 `ClientStatusCard` ("Kde to je") is DELETED, not left unrendered, and so
 * are its five locale keys (`staffConsole.statusCard.*`).
 *
 * It survived the flattening wave because every line in it was TRUE. What the
 * full-page shot showed is that none of them was NEW: the step and its number
 * are in the page eyebrow and again in the rail two hundred pixels away, whose
 * turn it is is the colour of the hero frame, and the next call is printed in
 * the right column. A card whose every fact is a copy is a card that makes the
 * reader check which copy is right.
 *
 * The one fact it owned - how many notes are waiting - is in the head of
 * Aktivita now, where those notes live, as a link rather than a number
 * (`ClientActivitySection`). A surviving key is an invitation to draw the card
 * again one panel at a time (F111), which is why they are gone from both
 * locales rather than merely unread.
 */

/** Rows 20 and 29-30's neighbour: who this client is and who works on them. */
function ClientAssignmentCard(props: ClientSectionsProps) {
  const { detail } = props;
  return (
    <article className="staff-card">
      <h3>{props.t("staffConsole.clientSettings")}</h3>
      <dl className="staff-definition">
        <div><dt>{props.t("staffConsole.email")}</dt><dd>{detail.client.email}</dd></div>
        <div><dt>{props.t("staffConsole.partner")}</dt><dd>{detail.client.partner?.name ?? props.t("staffConsole.assignment.partnerNone")}</dd></div>
        <div><dt>{props.t("staffConsole.owner")}</dt><dd>{detail.client.owner?.email ?? props.t("staffConsole.none")}</dd></div>
        {/* F143: the axis first, because it decides how many steps the whole
            page below is about. Without it an admin cannot tell an imported
            client from one who simply has not started. */}
        {/* 🔴 `journeyModeOf`, never the raw value: `t()` THROWS on a missing
            key, so a payload from an older bundle (or any value outside the
            two) would take this whole surface down over one axis. */}
        <div><dt>{props.t("staffConsole.journeyMode")}</dt><dd>{props.t(`staffConsole.journey.${journeyModeOf(detail.client.axes.journeyMode)}`)}</dd></div>
        <div><dt>{props.t("staffConsole.instanceMode")}</dt><dd>{props.t(`staffConsole.axis.instance_${detail.client.axes.instanceStartMode}`)}</dd></div>
        <div><dt>{props.t("staffConsole.purchaseMode")}</dt><dd>{props.t(`staffConsole.axis.purchase_${detail.client.axes.purchaseSource}`)}</dd></div>
        <div><dt>{props.t("staffConsole.appMode")}</dt><dd>{props.t(detail.client.axes.includesApp ? "staffConsole.axis.app_yes" : "staffConsole.axis.app_no")}</dd></div>
        {detail.client.axes.includesApp && (
          <div>
            <dt>{props.t("staffConsole.appWizard")}</dt>
            <dd>
              {detail.appWizard?.submitted ? (
                <span className="staff-app-submitted">
                  {props.t("staffConsole.appWizardSubmitted")}
                  {detail.appWizard.submittedAt ? ` (${formatDate(detail.appWizard.submittedAt, props.locale)})` : ""}
                </span>
              ) : detail.appWizard ? (
                props.t("staffConsole.appWizardProgress").replace("{percent}", String(detail.appWizard.progress.percent))
              ) : (
                props.t("staffConsole.appWizardEmpty")
              )}
            </dd>
          </div>
        )}
      </dl>
      {/* The one control that assigns, at the top of Nastaveni where the
          partner is named, admin only (Tomas Pohl 2026-09-17). The whole-row
          editor below keeps its select for the case where the edit is part of
          a wider correction. */}
      {props.role === "admin" && <PartnerAssign key={`assign-${detail.client.id}`} {...paneProps(props)} />}
    </article>
  );
}

/** The panes below still take the shared pane shape; the sections pass their
 * own props through unchanged so nothing re-derives anything. */
function paneProps(props: ClientSectionsProps): StaffPaneProps {
  return {
    detail: props.detail,
    role: props.role as StaffRole,
    locale: props.locale,
    t: props.t,
    run: props.run,
    refresh: props.refresh,
    csrfToken: props.csrfToken,
    onOpenStep: props.onOpenStep,
    currentUserId: props.currentUserId
  };
}
